/** @jsxImportSource @emotion/react */
import { PageWrapper } from "../../components/page-skeleton/PageWrapper";
import { GoBackLinkButton } from "../../components/GoBackLinkButton";
import { routes } from "../../appRoutingHelpers";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import {
  useGetMenu,
  useGetMenuOverwrites,
  useGetMenuSalesChannels,
  useGetSalesChannelsUsingMenu,
  useUpdateMenu,
} from "./service/menus-api";
import { Outlet, useParams } from "react-router-dom";
import { css } from "@emotion/react";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import { UpdateMenuSelector } from "./components/UpdateMenuSelector";
import { SpinnerBlock } from "../common/Spinner";
import { PushMenuToSalesChannelsSelector } from "./components/PushMenuToSalesChannelsSelector";
import { CardBody } from "../../components/page-skeleton/CardBody";
import { StatusIndicator } from "../../components/StatusIndicator";
import { colors } from "../../components/colors";
import { StyledText } from "../../assets/typography/typography";
import { Avatar, Tooltip } from "antd";
import { SecondaryNavigationLink } from "../../components/SecondaryNavigationLink";
import { getCountryInfo } from "../../service/countries-service";
import { EllipsisDropdown } from "../../components/EllipsisDropdown";
import { toastr } from "react-redux-toastr";
import { DropdownItemButton } from "../common/DropdownItemButton";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";
import { ActionSpinner } from "../../components/ActionSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuInfo } from "./models/Menus";
import {
  getFormattedCurrency,
  orgsWithAutoCampaigns,
  useIsOrganization,
} from "../../util";
import { DotDivider } from "../../components/DotDivider";
import { config } from "../../config";

const additionalContainerStyle = css`
  padding-bottom: 24px;
`;

const flagWrapperStyle = css`
  display: flex;
  padding: 24px 24px 0px 24px;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const countryAndCurrencyWrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const buttonWrapper = css`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const activeRestaurantWrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const tabsWrapperStyle = css`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  width: 100%;
  max-width: 1200px;
`;

const menuAvailabilityWrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

export const MenuUpsertLayout = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const showAutoCampaigns_ = useIsOrganization(orgsWithAutoCampaigns);

  const { data, isLoading } = useGetMenu(organizationKeyName, id);

  const {
    data: menuSalesChannels,
    isLoading: menuSalesChannelsIsLoading,
    error: getMenuSalesChannelsError,
  } = useGetMenuSalesChannels(organizationKeyName, id);

  const {
    data: salesChannels,
    error: getSalesChannelsError,
  } = useGetSalesChannelsUsingMenu(organizationKeyName, id);

  const { mutateAsync: updateMenu, isLoading: updateIsLoading } = useUpdateMenu(
    id
  );

  const {
    data: menuOverwrites,
    isLoading: menuOverwritesIsLoading,
  } = useGetMenuOverwrites(organizationKeyName, id);

  const activeOnMoreThanZeroRestaurants =
    salesChannels && salesChannels?.length > 0;

  const showAutoCampaigns =
    config.environment === "production" ? showAutoCampaigns_ : true;

  useHandleFetchError(getMenuSalesChannelsError);
  useHandleFetchError(getSalesChannelsError);

  if (
    isLoading ||
    menuSalesChannelsIsLoading ||
    !data ||
    menuOverwritesIsLoading
  ) {
    return <SpinnerBlock />;
  }

  return (
    <>
      <PageWrapper>
        <GoBackLinkButton
          to={routes.menuList(organizationKeyName)}
          text="Back to all menus"
        />
        <CardContainer additionalStyling={additionalContainerStyle}>
          <div css={flagWrapperStyle}>
            <div css={countryAndCurrencyWrapperStyle}>
              <StyledText
                className="bodyTextRegular mb-0"
                color={colors.textPrimary}
              >
                {getCountryInfo(data.countryAlpha2Code).emojiFlag}{" "}
                {getCountryInfo(data.countryAlpha2Code).name}
              </StyledText>
              {data.currency && (
                <>
                  <DotDivider />
                  <StyledText
                    className="bodyTextRegular mb-0"
                    color={colors.textPrimary}
                  >
                    {getFormattedCurrency(0, data.currency)}
                  </StyledText>
                </>
              )}
            </div>
            {!data.isMenuOverwritesEnabled && (
              <div css={menuAvailabilityWrapperStyle}>
                <StatusIndicator
                  backgroundColor={
                    data.isMenuOverwritesEnabled
                      ? colors.successMedium
                      : colors.greyBorder
                  }
                />
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  Menu scheduling disabled
                </StyledText>
                <EllipsisDropdown
                  menu={{
                    items: [
                      {
                        key: "1",
                        label: (
                          <DropdownItemButton
                            onClick={async () => {
                              const newData: MenuInfo = {
                                countryCode: data.countryAlpha2Code,
                                name: data.name,
                                internalNote: data.internalNote || "",
                                isMenuOverwritesEnabled: !data.isMenuOverwritesEnabled,
                                currency: data.currency,
                              };
                              try {
                                await updateMenu({
                                  id: id!,
                                  organizationKeyName,
                                  data: newData,
                                });
                                toastr.success("Success", "Menu updated");
                              } catch (error: any) {
                                const message = getErrorMessage(error);
                                toastr.error("Error", message);
                              }
                            }}
                            type="button"
                            icon={
                              updateIsLoading ? (
                                <ActionSpinner />
                              ) : data.isMenuOverwritesEnabled ? (
                                <FontAwesomeIcon icon={solid("do-not-enter")} />
                              ) : (
                                <FontAwesomeIcon icon={solid("check-circle")} />
                              )
                            }
                            disabled={updateIsLoading}
                          >
                            Enable menu scheduling
                          </DropdownItemButton>
                        ),
                      },
                    ],
                  }}
                />
              </div>
            )}
          </div>
          <PageHeader
            title={data?.name}
            titleButton={<UpdateMenuSelector menu={data} />}
          >
            <div css={buttonWrapper}>
              {(menuSalesChannels || []).filter((sc) => sc.menuPushable)
                .length > 0 && (
                <PushMenuToSalesChannelsSelector buttonType="regular" id={id} />
              )}
            </div>
          </PageHeader>
          <CardBody>
            <div css={activeRestaurantWrapperStyle}>
              {activeOnMoreThanZeroRestaurants && (
                <StatusIndicator backgroundColor={colors.successMedium} />
              )}
              <StyledText
                className="bodyTextRegular mb-0"
                color={
                  activeOnMoreThanZeroRestaurants
                    ? colors.textPrimary
                    : colors.textSecondary
                }
              >
                {activeOnMoreThanZeroRestaurants
                  ? "Active on"
                  : "This menu is not active on any restaurants."}
              </StyledText>
              {activeOnMoreThanZeroRestaurants && (
                <Avatar.Group
                  maxCount={8}
                  maxStyle={{
                    color: colors.ctaMain,
                    backgroundColor: colors.ctaLight,
                  }}
                >
                  {salesChannels?.map((salesChannel, i) => (
                    <Tooltip
                      key={i}
                      title={`${salesChannel.name}, ${salesChannel?.shop.title}`}
                      placement="top"
                    >
                      <Avatar src={salesChannel.shop.imageUrl} />
                    </Tooltip>
                  ))}
                </Avatar.Group>
              )}
            </div>
          </CardBody>
        </CardContainer>
        <div css={tabsWrapperStyle}>
          <SecondaryNavigationLink
            to={routes.menuEdit(organizationKeyName, id)}
            end={true}
          >
            Menu products
          </SecondaryNavigationLink>
          <SecondaryNavigationLink
            to={routes.menuEditRecommendedProducts(organizationKeyName, id)}
            end={true}
          >
            Recommended products
          </SecondaryNavigationLink>
          {data.isMenuOverwritesEnabled && (
            <SecondaryNavigationLink
              to={routes.menuEditMenuAvailability(organizationKeyName, id)}
              end={true}
            >
              Menu scheduling ({menuOverwrites?.length})
            </SecondaryNavigationLink>
          )}
          {showAutoCampaigns && (
            <SecondaryNavigationLink
              to={routes.menuEditCampaigns(organizationKeyName, id)}
              end={true}
            >
              Auto campaigns
            </SecondaryNavigationLink>
          )}
        </div>
      </PageWrapper>
      <Outlet />
    </>
  );
};
